import { useEffect, useState } from "react";
import { UseFormReturn, Controller } from "react-hook-form";
import Button from "@/components/Button";
import Checkbox from "@/components/Checkbox";
import EmailPreviewDialog from "@/components/EmailPreviewDialog";
import Select from "@/components/Select/Select";
import TextField from "@/components/TextField/TextField";
import { JobPostCloseFormInput } from "../types";
import { employerOutcomeSupportingText, yesNoNotSetOptions } from "../../types";
import { useEmployerSatisfactionSurveyEmailPreview } from "../../_hooks/useEmployerSatisfactionSurveyEmailPreview";

interface EmployerInformationStepProps {
  form: UseFormReturn<JobPostCloseFormInput>;
  jobPostId: string;
}

export function EmployerInformationStep({ form, jobPostId }: EmployerInformationStepProps) {
  const {
    control,
    watch,
    formState: { errors },
    clearErrors
  } = form;
  const sendEmployerSurvey = watch("sendEmployerSurvey");
  const [previewOpen, setPreviewOpen] = useState(false);
  const {
    loadPreview,
    resetPreview,
    preview,
    loading: previewLoading,
    error: previewError
  } = useEmployerSatisfactionSurveyEmailPreview(jobPostId);

  useEffect(() => () => resetPreview(), [resetPreview]);

  const handlePreviewEmail = () => {
    setPreviewOpen(true);
    void loadPreview();
  };

  // Helper function to safely convert error messages to string
  const getErrorMessage = (error: any): string | undefined => {
    if (!error) return undefined;
    if (typeof error === "string") return error;
    if (error.message && typeof error.message === "string") return error.message;
    return String(error);
  };

  return (
    <div className="space-y-6">
      <div className="text-headline-small">Enter employer details to close the job.</div>

      <div>
        <div className="text-title-medium">Facility Information</div>

        <div className="space-y-6 mt-6">
          <Controller
            name="careerConsultantVisited"
            control={control}
            defaultValue="Not Set"
            rules={{ required: "Please select an option" }}
            render={({ field }) => (
              <Select
                label="Facility Visited"
                required
                error={!!errors.careerConsultantVisited}
                errorText={getErrorMessage(errors.careerConsultantVisited?.message)}
                options={yesNoNotSetOptions}
                onSelect={(value: string) => {
                  field.onChange(value);
                  clearErrors("careerConsultantVisited");
                }}
                value={field.value}
                supportingText={employerOutcomeSupportingText.facilityVisited}
              />
            )}
          />

          <Controller
            name="isNetNewPosition"
            control={control}
            defaultValue="Not Set"
            render={({ field }) => (
              <Select
                label="New Position at Facility"
                options={yesNoNotSetOptions}
                onSelect={(value: string) => {
                  field.onChange(value);
                  clearErrors("isNetNewPosition");
                }}
                value={field.value}
                supportingText={employerOutcomeSupportingText.newPositionAtFacility}
              />
            )}
          />

          <Controller
            name="isPgaFacilityConversion"
            control={control}
            defaultValue="Not Set"
            rules={{ required: "Please select an option" }}
            render={({ field }) => (
              <Select
                label="Facility Conversion"
                required
                error={!!errors.isPgaFacilityConversion}
                errorText={getErrorMessage(errors.isPgaFacilityConversion?.message)}
                options={yesNoNotSetOptions}
                onSelect={(value: string) => {
                  field.onChange(value);
                  clearErrors("isPgaFacilityConversion");
                }}
                value={field.value}
                supportingText={employerOutcomeSupportingText.facilityConversion}
              />
            )}
          />

          <Controller
            name="isPgaJobPlacementConversion"
            control={control}
            defaultValue="Not Set"
            rules={{ required: "Please select an option" }}
            render={({ field }) => (
              <Select
                label="PGA Placement Conversion"
                required
                error={!!errors.isPgaJobPlacementConversion}
                errorText={getErrorMessage(errors.isPgaJobPlacementConversion?.message)}
                options={yesNoNotSetOptions}
                onSelect={(value: string) => {
                  field.onChange(value);
                  clearErrors("isPgaJobPlacementConversion");
                }}
                value={field.value}
                supportingText={employerOutcomeSupportingText.pgaPlacementConversion}
              />
            )}
          />
        </div>
      </div>

      <div className="space-y-2">
        <div className="text-title-medium">Employer Feedback</div>
        <div className="text-body-medium">
          If checked, the Employer associated with the job post will be emailed a survey asking about their experience
          with the PGA Job Board.
        </div>
        <div className="flex flex-col gap-6">
          <Controller
            name="sendEmployerSurvey"
            control={control}
            defaultValue={true}
            render={({ field: { onChange, value } }) => (
              <Checkbox
                name="sendEmployerSurvey"
                label="Send Employer Satisfaction Survey Email"
                checked={value}
                onChange={onChange}
              />
            )}
          />
          <div>
            <Button
              variant="text"
              icon="mail"
              onClick={handlePreviewEmail}
              disabled={!sendEmployerSurvey}
              testId="preview-email-button"
            >
              Preview Email
            </Button>
          </div>
        </div>
      </div>

      <div className="space-y-2">
        <div className="text-title-medium">Job Post Closing Comments</div>
        <div className="text-body-medium">Provide any additional context that should be kept with this job post.</div>
        <Controller
          name="notes"
          control={control}
          render={({ field }) => (
            <TextField
              type="textarea"
              rows={6}
              label="Closing Comments"
              value={field.value || ""}
              onChangeText={field.onChange}
              error={!!errors.notes}
              errorText={getErrorMessage(errors.notes?.message)}
              className="w-full"
            />
          )}
        />
      </div>
      <EmailPreviewDialog
        open={previewOpen}
        onClosed={() => setPreviewOpen(false)}
        subject={previewLoading ? undefined : preview?.subject}
        html={previewLoading ? undefined : preview?.html}
        loading={previewLoading}
        errorMessage={previewLoading ? null : (previewError?.message ?? null)}
      />
    </div>
  );
}
